import { CTABand } from "../../components/site/CTABand";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PackageCheck, Truck, CheckCircle2, ClipboardCheck, Search } from "lucide-react";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { NetworkCanvas } from "../../components/NetworkCanvas";
import { FadeIn, MaskedLine } from "../../components/motion/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (d) => new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric" });

const KIND_LABEL = { assessment: "Site Assessment", invoice: "Invoice", product: "Store Order" };

const Steps = ({ fulfilment }) => {
  const steps = [
    { key: "confirmed", label: "Confirmed", icon: ClipboardCheck },
    { key: "dispatched", label: "Dispatched", icon: Truck },
    { key: "delivered", label: "Delivered", icon: CheckCircle2 },
  ];
  const activeIdx = steps.findIndex((s) => s.key === fulfilment);
  return (
    <div className="flex items-center gap-0 mt-3" data-testid="order-steps">
      {steps.map((s, i) => {
        const Icon = s.icon;
        const done = activeIdx >= i;
        return (
          <div key={s.key} className="flex items-center">
            <div className="flex flex-col items-center gap-1.5">
              <span className={`w-8 h-8 flex items-center justify-center border transition-colors ${done ? "border-nx-gold bg-nx-gold/15 text-nx-goldlight" : "border-white/15 text-slate-600"}`}>
                <Icon size={14} />
              </span>
              <span className={`text-[10px] tracking-[0.1em] uppercase ${done ? "text-nx-goldlight" : "text-slate-600"}`}>{s.label}</span>
            </div>
            {i < steps.length - 1 && <span className={`h-px w-10 sm:w-16 mx-1 mb-5 ${activeIdx > i ? "bg-nx-gold/60" : "bg-white/10"}`} />}
          </div>
        );
      })}
    </div>
  );
};

export default function TrackOrder() {
  const [form, setForm] = useState({ phone: "", email: "" });
  const [orders, setOrders] = useState(null);
  const [busy, setBusy] = useState(false);

  const track = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/orders/track`, form);
      setOrders(data.orders);
    } catch {
      toast.error("Could not look up orders — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors";

  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased min-h-screen flex flex-col" data-testid="track-order-page">
      <SiteHeader />
      <section className="relative overflow-hidden pt-20 sm:pt-24 lg:pt-36 pb-12 flex-1">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,#10203a_0%,#0a1526_60%,#060d1a_100%)]" />
        <NetworkCanvas skyline={false} className="absolute inset-0 w-full h-full opacity-40" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,rgba(6,13,26,0.8)_100%)]" />
        <div className="relative nx-wrap max-w-2xl">
          <FadeIn delay={0.15}>
            <div className="flex items-center gap-4 mb-5">
              <span className="h-px w-12 bg-nx-gold/70" />
              <span className="text-[11px] sm:text-xs tracking-[0.3em] uppercase text-nx-goldlight">Order Tracking</span>
            </div>
          </FadeIn>
          <h1 className="font-marcellus text-4xl sm:text-5xl text-white leading-[1.06]" data-testid="track-title">
            <MaskedLine delay={0.3}>Track your</MaskedLine>
            <MaskedLine delay={0.45} innerClassName="text-nx-goldlight">order.</MaskedLine>
          </h1>
          <FadeIn delay={0.7}>
            <p className="mt-5 text-sm text-slate-400 leading-relaxed max-w-lg">Enter the phone number and email you used while paying — we show every order and its delivery status.</p>
          </FadeIn>

          <FadeIn delay={0.85}>
            <form onSubmit={track} className="mt-8 border border-white/10 bg-white/[0.02] p-6 sm:p-8 flex flex-col gap-4" data-testid="track-form">
              <input required type="tel" placeholder="Phone number used while ordering" className={input} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="track-phone-input" />
              <input required type="email" placeholder="Email used while ordering" className={input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="track-email-input" />
              <button type="submit" disabled={busy} className="flex items-center justify-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold tracking-wide text-sm px-8 py-4 transition-colors disabled:opacity-60" data-testid="track-submit-button">
                <Search size={15} /> {busy ? "Looking up…" : "Track My Order"}
              </button>
            </form>
          </FadeIn>

          {orders !== null && (
            <div className="mt-8 flex flex-col gap-4" data-testid="track-results">
              {orders.length === 0 ? (
                <div className="border border-dashed border-white/10 p-8 text-center" data-testid="track-empty">
                  <PackageCheck size={28} className="text-slate-600 mx-auto mb-3" strokeWidth={1.5} />
                  <p className="text-sm text-slate-400">No orders found for this phone + email combination.</p>
                  <p className="text-xs text-slate-600 mt-2">Check the details are exactly what you entered during payment.</p>
                </div>
              ) : (
                orders.map((o, i) => (
                  <div key={i} className="border border-white/10 bg-white/[0.02] p-6" data-testid={`track-order-${i}`}>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                      <span className="text-[10px] tracking-[0.14em] uppercase text-nx-goldlight border border-nx-gold/40 px-2.5 py-1">{KIND_LABEL[o.kind] || o.kind}</span>
                      <span className={`text-[10px] tracking-[0.14em] uppercase border px-2.5 py-1 ${o.status === "paid" ? "text-emerald-300 border-emerald-500/50" : o.status === "failed" ? "text-red-400 border-red-500/50" : "text-amber-300 border-amber-500/40"}`} data-testid={`track-status-${i}`}>
                        {o.status === "paid" ? "Paid" : o.status === "created" ? "Payment pending" : o.status}
                      </span>
                      <span className="text-white font-semibold ml-auto">₹{o.amount_inr.toLocaleString("en-IN")}</span>
                    </div>
                    <p className="text-sm text-slate-300 mt-3">{o.reference}</p>
                    <p className="text-xs text-slate-500 mt-1">Ordered {fmtDate(o.created_at)}{o.paid_at ? ` · Paid ${fmtDate(o.paid_at)}` : ""}</p>
                    {o.status === "paid" && <Steps fulfilment={o.fulfilment} />}
                    {o.status === "paid" && !o.fulfilment && <p className="text-xs text-slate-500 mt-3">Payment received — our team will confirm your order shortly.</p>}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </section>
      <CTABand />
      <SiteFooter />
    </div>
  );
}
